import type { CorridorDefinition } from "@/types/corridor";
import heroVideo from "@/assets/plain.mp4";

const SEO_OG =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/64ab5ad3-4f6e-457e-94c1-24567a339f8d/id-preview-1268a46e--a6ccb7a0-9a40-44ba-99d3-be21b3278db5.lovable.app-1774604645516.png";

type PopularCorridorSeed = {
  slug: string;
  cityA: string;
  cityB: string;
  distanceKm: number;
  driveTime: string;
  highway: string;
  busFare: number;
  cabFare: number;
  carpoolFare: number;
  monthlySavings: number;
  ridesToday: number;
  completedRides: number;
  zonesA: string[];
  zonesB: string[];
  midTitle: string;
  midSubtitle: string;
  localIntro: string;
  problemHeadline: string;
  problemSub: string;
};

function buildCorridor(seed: PopularCorridorSeed): CorridorDefinition {
  const { cityA, cityB, slug } = seed;
  return {
    slug,
    cityA,
    cityB,
    stats: {
      distanceKm: seed.distanceKm,
      driveTime: seed.driveTime,
      highway: seed.highway,
      zonesA: seed.zonesA,
      zonesB: seed.zonesB,
      busFare: seed.busFare,
      cabFare: seed.cabFare,
      carpoolFare: seed.carpoolFare,
      monthlySavings: seed.monthlySavings,
      ridesToday: seed.ridesToday,
      completedRides: seed.completedRides,
      avgRating: 4.75,
      travellers: Math.round(seed.completedRides * 2.1),
    },
    heroImageSrc: "/Chalo-uploads/ed3e4cb1-8d78-4271-9f34-a0f4c209dca5.png",
    heroImageAlt: `Intercity ridesharing corridor ${cityA} to ${cityB}`,
    heroVideoSrc: heroVideo,
    seoOgImage: SEO_OG,
    midCta: { title: seed.midTitle, subtitle: seed.midSubtitle },
    localLife: {
      eyebrow: "Along the corridor",
      title: "Local life & travel tips",
      intro: seed.localIntro,
    },
    problemSolution: {
      headline: seed.problemHeadline,
      sub: seed.problemSub,
    },
    spots: [
      {
        icon: "landmark",
        title: `${cityA} pickups`,
        meta: cityA,
        tag: "Start zones",
        text: `Popular clusters for verified hosts starting from ${cityA}.`,
        featured: true,
      },
      {
        icon: "utensils",
        title: "Highway meals",
        meta: seed.highway,
        tag: "Break stops",
        text: "Tea, breakfast, and quick stops keep long drives comfortable.",
      },
      {
        icon: "building2",
        title: `${cityB} drops`,
        meta: cityB,
        tag: "Arrival",
        text: `Flexible drop zones across ${cityB} for last-mile convenience.`,
      },
    ],
    happenings: [
      {
        when: "Weekends",
        title: "Peak shared rides",
        where: `${cityA} ↔ ${cityB}`,
        text: "Friday evenings and Sunday returns fill fastest — book early.",
      },
      {
        when: "Weekdays",
        title: "Work & student travel",
        where: seed.highway,
        text: "Morning departures are popular for commuters and campus runs.",
      },
    ],
    testimonials: [
      {
        name: "Ananya R.",
        role: "Intercity traveller",
        rating: 5,
        text: `Reliable shared seats on ${cityA}–${cityB} — fair pricing and verified co-travellers.`,
      },
    ],
    faqs: [
      {
        q: `Is carpooling safe between ${cityA} and ${cityB}?`,
        a: "Yes. Duri verifies profiles, shows ratings and trip details, and supports in-app chat before you confirm.",
      },
      {
        q: `How much can I save vs a cab on ${cityA}–${cityB}?`,
        a: `Shared seats typically start near ₹${seed.carpoolFare}, well below private cab fares on this corridor.`,
      },
      {
        q: "Are return rides available?",
        a: `Yes. Browse both ${cityA} → ${cityB} and ${cityB} → ${cityA} in the app.`,
      },
    ],
  };
}

/** Popular routes grid (after Hyderabad ↔ Vijayawada / Guntur) — each has `/route/:slug` + JSON mock. */
export const additionalPopularCorridors: CorridorDefinition[] = [
  buildCorridor({
    slug: "hyderabad-khammam",
    cityA: "Hyderabad",
    cityB: "Khammam",
    distanceKm: 195,
    driveTime: "3–4 hrs",
    highway: "NH65",
    busFare: 420,
    cabFare: 3200,
    carpoolFare: 329,
    monthlySavings: 2400,
    ridesToday: 9,
    completedRides: 620,
    zonesA: ["Hitech City", "Gachibowli", "LB Nagar", "Uppal"],
    zonesB: ["Wyra Road", "Raghunathpalem", "Khammam Fort area", "NSP Colony"],
    midTitle: "Share seats to Khammam",
    midSubtitle: "NH65 corridor — split tolls and fuel with verified travellers.",
    localIntro:
      "A busy Telangana stretch for family visits, work travel, and student shuttles — ideal for affordable carpooling.",
    problemHeadline: "Driving solo Hyderabad–Khammam adds up",
    problemSub: "List spare seats or grab one — Duri keeps intercity travel fair and social.",
  }),
  buildCorridor({
    slug: "hyderabad-bangalore",
    cityA: "Hyderabad",
    cityB: "Bangalore",
    distanceKm: 570,
    driveTime: "9–11 hrs",
    highway: "NH44",
    busFare: 1200,
    cabFare: 12000,
    carpoolFare: 1299,
    monthlySavings: 5500,
    ridesToday: 11,
    completedRides: 890,
    zonesA: ["Hitech City", "Gachibowli", "Kondapur", "Miyapur"],
    zonesB: ["Whitefield", "Electronic City", "Koramangala", "Manyata"],
    midTitle: "Hyderabad–Bengaluru without the cab shock",
    midSubtitle: "Overnight and morning pools — verified hosts and clear seat pricing.",
    localIntro:
      "India’s busiest tech corridor. Shared rides help beat peak cab fares and empty back seats.",
    problemHeadline: "Long haul shouldn’t mean long bills",
    problemSub: "Share the drive on NH44 with Duri — lower cost per seat, verified profiles.",
  }),
  buildCorridor({
    slug: "vijayawada-visakhapatnam",
    cityA: "Vijayawada",
    cityB: "Visakhapatnam",
    distanceKm: 350,
    driveTime: "6–7.5 hrs",
    highway: "NH16",
    busFare: 650,
    cabFare: 6500,
    carpoolFare: 549,
    monthlySavings: 3800,
    ridesToday: 10,
    completedRides: 740,
    zonesA: ["Benz Circle", "Auto Nagar", "PVP Mall", "Gunadala"],
    zonesB: ["RK Beach", "MVP Colony", "Gajuwaka", "Rushikonda"],
    midTitle: "Coastal Andhra, shared",
    midSubtitle: "Beach runs and port-city travel — offer seats or book one.",
    localIntro:
      "Coastal highway scenery and steady weekend demand — great for hosts with spare seats.",
    problemHeadline: "Vijayawada–Visakhapatnam rides cost too much solo",
    problemSub: "Pool on NH16 with transparent pricing and ID-verified co-travellers.",
  }),
  buildCorridor({
    slug: "vijayawada-guntur",
    cityA: "Vijayawada",
    cityB: "Guntur",
    distanceKm: 35,
    driveTime: "45–70 min",
    highway: "NH16",
    busFare: 80,
    cabFare: 900,
    carpoolFare: 99,
    monthlySavings: 2200,
    ridesToday: 22,
    completedRides: 2100,
    zonesA: ["Benz Circle", "PNB", "Auto Nagar", "Enikepadu"],
    zonesB: ["Brodipet", "Arundelpet", "Nallapadu", "Brindavan Gardens"],
    midTitle: "Short hop, smart share",
    midSubtitle: "Daily demand between Vijayawada and Guntur — fill empty seats in minutes.",
    localIntro:
      "High-frequency corridor for college, business, and family hops — perfect for micro-carpools.",
    problemHeadline: "Why pay cab fare for a 45-minute hop?",
    problemSub: "Duri matches quick Vijayawada–Guntur pools with fair per-seat pricing.",
  }),
  buildCorridor({
    slug: "vijayawada-nellore",
    cityA: "Vijayawada",
    cityB: "Nellore",
    distanceKm: 280,
    driveTime: "5–6 hrs",
    highway: "NH16",
    busFare: 550,
    cabFare: 5200,
    carpoolFare: 449,
    monthlySavings: 3000,
    ridesToday: 8,
    completedRides: 510,
    zonesA: ["Benz Circle", "Kanuru", "Gunadala", "Poranki"],
    zonesB: ["Rameswaram Camp", "Magunta Layout", "Vedayapalem", "Nellore Bus Stand"],
    midTitle: "NH16 to Nellore",
    midSubtitle: "Rice bowl belt travel — share fuel on long straight stretches.",
    localIntro:
      "Popular for festivals, medical visits, and coastal connections — rides fill on long weekends.",
    problemHeadline: "Solo drives on NH16 add toll + fuel stress",
    problemSub: "Split costs fairly with Duri carpooling between Vijayawada and Nellore.",
  }),
  buildCorridor({
    slug: "nellore-chennai",
    cityA: "Nellore",
    cityB: "Chennai",
    distanceKm: 180,
    driveTime: "3.5–4.5 hrs",
    highway: "NH16",
    busFare: 380,
    cabFare: 4200,
    carpoolFare: 349,
    monthlySavings: 2800,
    ridesToday: 13,
    completedRides: 960,
    zonesA: ["Nellore Bus Stand", "Magunta", "Vedayapalem", "Muthukur"],
    zonesB: ["OMR", "Tambaram", "Velachery", "Kilpauk"],
    midTitle: "Nellore–Chennai pools",
    midSubtitle: "Coastal highway into the metro — morning and evening demand.",
    localIntro:
      "Students, IT commuters, and patients use this corridor daily — verified sharing cuts spend.",
    problemHeadline: "Chennai-bound shouldn’t mean cab-only",
    problemSub: "Book or offer seats on Nellore–Chennai with Duri’s verified marketplace.",
  }),
  buildCorridor({
    slug: "nellore-tirupati",
    cityA: "Nellore",
    cityB: "Tirupati",
    distanceKm: 135,
    driveTime: "2.5–3.5 hrs",
    highway: "NH16 / NH140",
    busFare: 280,
    cabFare: 2800,
    carpoolFare: 229,
    monthlySavings: 1800,
    ridesToday: 15,
    completedRides: 1200,
    zonesA: ["Nellore Bus Stand", "Sullurpeta", "Gudur", "Naidupeta"],
    zonesB: ["Alipiri", "Tiruchanoor", "Renigunta", "Chittoor Road"],
    midTitle: "Pilgrimage & family hops",
    midSubtitle: "High demand around festivals — share seats to Tirupati responsibly.",
    localIntro:
      "Spiritual travel peaks on weekends; carpooling reduces congestion and parking stress.",
    problemHeadline: "Solo taxis to Tirupati spike on festival weeks",
    problemSub: "Pool with verified hosts from Nellore — predictable seat pricing.",
  }),
  buildCorridor({
    slug: "chennai-tirupati",
    cityA: "Chennai",
    cityB: "Tirupati",
    distanceKm: 135,
    driveTime: "3–4 hrs",
    highway: "NH48 / NH140",
    busFare: 320,
    cabFare: 3500,
    carpoolFare: 279,
    monthlySavings: 2200,
    ridesToday: 18,
    completedRides: 1550,
    zonesA: ["Tambaram", "Chromepet", "OMR", "Kilpauk"],
    zonesB: ["Alipiri", "Tiruchanoor", "Renigunta", "Tada"],
    midTitle: "Chennai–Tirupati carpool",
    midSubtitle: "Weekend darshan demand — offer empty seats or grab one early.",
    localIntro:
      "One of South India’s busiest devotional corridors — ideal for verified ride sharing.",
    problemHeadline: "Peak-season cab fares hurt pilgrims",
    problemSub: "Duri connects Chennai–Tirupati travellers with fair, verified shared rides.",
  }),
  buildCorridor({
    slug: "bangalore-chittoor",
    cityA: "Bengaluru",
    cityB: "Chittoor",
    distanceKm: 165,
    driveTime: "3–4 hrs",
    highway: "NH75 / NH48",
    busFare: 350,
    cabFare: 3800,
    carpoolFare: 299,
    monthlySavings: 2400,
    ridesToday: 12,
    completedRides: 680,
    zonesA: ["Electronic City", "Koramangala", "Whitefield", "Silk Board"],
    zonesB: ["Chittoor Bus Stand", "Greamspet", "Palamaner Road", "Bangalore Road"],
    midTitle: "Bengaluru–Chittoor shared rides",
    midSubtitle: "Border corridor pools — split fuel on NH75 with verified travellers.",
    localIntro:
      "A steady Andhra–Karnataka link for family visits, campus travel, and weekend returns — ideal for fair carpooling.",
    problemHeadline: "Solo cabs on Bengaluru–Chittoor add up fast",
    problemSub: "Share seats with Duri — verified hosts, clear pricing, both directions.",
  }),
];
